import { memo, useContext, useEffect, useRef, useState, type RefObject } from 'react';
import { Button, ProgressBar, Spinner, Row, Col } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { webSocketUrl } from '@/app/common/api/config';
import { Centrifuge } from 'centrifuge';
import { useGenerateTokenMutation } from '@/app/common/api/socketApi';
import { PauseMassiveContext } from '@/context/Context';
import massiveApi from '@/app/KeywordResearch/api/massiveApi';
import IconAlert from '@/components/common/IconAlert';

type MassiveProgressGridRef = RefObject<{
  api?: {
    applyTransaction: (transaction: { add?: unknown[] }) => void;
  };
} | null>;

type MassiveProgressBarProps = {
  lastStatus: number;
  task_id: string;
  gridRef: MassiveProgressGridRef;
  onTaskFinished?: (keywordCount: number) => void;
};

type MassiveSocketProgressPayload = {
  all?: number;
  done?: number;
  request_status_code?: number;
  request_status?: number | string;
  keywords?: unknown[];
};

type MassiveSocketProgressMessage = {
  data?: {
    data?: MassiveSocketProgressPayload;
  };
};

const MassiveProgressBar = ({
  lastStatus,
  task_id,
  gridRef,
  onTaskFinished
}: MassiveProgressBarProps) => {
  const dispatch = useDispatch();
  const hasFinishedRef = useRef(false);
  const streamedKeywordCountRef = useRef(0);
  const { setShowStopModal, setTaskId } = useContext(PauseMassiveContext);
  const [token, setToken] = useState<string | null>(null);
  const [progressData, setProgressData] = useState<
    MassiveSocketProgressMessage | undefined
  >();
  const [generateToken] = useGenerateTokenMutation();
  const progressPayload = progressData?.data?.data;
  const progressBarPercent = progressPayload?.all
    ? Math.floor((100 / progressPayload.all) * (progressPayload.done ?? 0))
    : 0;

  useEffect(() => {
    const fetchToken = async () => {
      await generateToken({
        id: task_id,
        channels: [`personal:#${task_id}`]
      })
        .unwrap()
        .then(response => {
          setToken(response.data);
        })
        .catch(err => {
          console.error('Error fetching data:', err);
        });
    };

    fetchToken();
  }, [generateToken, task_id]);

  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!token) return;

    const centrifugo = new Centrifuge(webSocketUrl, {
      getToken: async () => token
    });

    centrifugo.on('connecting', () => {
      console.log('Connecting to socket');
    });

    centrifugo.on('connected', () => {
      setConnected(true);
      console.log('Connected to socket');
    });

    centrifugo.on('disconnected', () => {
      console.log('Disconnected from socket');
      setConnected(false);
      dispatch(massiveApi.util.invalidateTags(['historyMassive']));
    });

    centrifugo.on('publication', message => {
      const socketMessage = message as MassiveSocketProgressMessage;
      const payload = socketMessage?.data?.data;

      if (!payload) return;

      if (payload.keywords && gridRef.current) {
        streamedKeywordCountRef.current += payload.keywords.length;
        gridRef.current.api?.applyTransaction({
          add: payload.keywords
        });
      }

      setProgressData(socketMessage);

      const isFinished =
        payload.request_status_code === 6 ||
        (payload.all !== undefined && payload.all === payload.done);

      if (isFinished && !hasFinishedRef.current) {
        hasFinishedRef.current = true;
        const reportedCount = Number(payload.request_status);
        const keywordCount = Number.isFinite(reportedCount)
          ? reportedCount
          : streamedKeywordCountRef.current;
        onTaskFinished?.(keywordCount);
        centrifugo.disconnect();
        setConnected(false);
        dispatch(
          massiveApi.util.invalidateTags(['historyMassive', 'massiveStatus'])
        );
      }
    });

    centrifugo.connect();

    return () => {
      centrifugo.disconnect();
    };
  }, [dispatch, gridRef, onTaskFinished, token]);

  if (!connected) return null;

  return progressPayload?.request_status_code !== 6 ? (
    <Row>
      <Col md={12}>
        <div className="d-flex flex-column flex-md-row align-items-stretch">
          <div className="flex-1">
            <div className="d-flex justify-content-between rounded bg-body-tertiary shadow-sm p-2 gap-1 align-items-center">
              <Spinner
                style={{
                  width: '20px',
                  marginBottom: '-3px',
                  paddingLeft: '3px',
                  height: '20px'
                }}
                className="me-2"
              />
              {progressData ? (
                <div className="w-100">
                  {progressPayload?.request_status_code === 2 &&
                    'درصف دریافت لطفا منتظر بمانید (هر نیم ساعت بررسی کنید) ... '}
                  {progressPayload?.request_status_code === 4 && (
                    <>
                      <div className="mb-1" style={{ fontSize: '14px' }}>
                        <span className="fw-black fs-1 mx-1">
                          {progressPayload.request_status}
                        </span>
                        کلمه یافت شد (درحال جستجو)
                        <span className="float-end">
                          {Math.floor(
                            (100 / (progressPayload.all ?? 1)) *
                              (progressPayload.done ?? 0)
                          )}
                          %
                        </span>
                      </div>
                      <ProgressBar
                        now={progressBarPercent}
                        style={{ height: 5 }}
                        className="rounded-pill align-middle"
                        variant="success"
                      />
                    </>
                  )}
                </div>
              ) : (
                <div className="w-100">
                  {lastStatus === 2
                    ? 'درصف دریافت لطفا منتظر بمانید (هر نیم ساعت بررسی کنید) ...'
                    : 'در حال اتصال برای دریافت اطلاعات جستجو ...'}
                </div>
              )}
            </div>
          </div>
          <Button
            onClick={() => {
              setShowStopModal(true);
              setTaskId(task_id);
            }}
            className="ms-md-2 mt-2 mt-md-0 ms-0 fs--1"
            variant="danger"
          >
            توقف جستجو
          </Button>
        </div>
      </Col>
    </Row>
  ) : (
    <IconAlert variant="success" dismissible>
      <p className="mb-0 fs--1">
        <span className="fw-black fs-1">{progressPayload?.request_status}</span>
        کلمه با موفقیت یافت شد. (پایان جستجو)
      </p>
    </IconAlert>
  );
};

export default memo(MassiveProgressBar);
